import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core'
import { AuthService } from './shared/services/auth.service'
import { Router } from '@angular/router'
import { ApiLoaderService } from './shared/services/api-loader.service'
import { TranslationService } from './shared/services/translation.service'
import { SessionService } from './shared/services/session-service'
import { ThemeService } from './shared/services/theme.service'
import { InfoModal } from './shared/components/info-modal/info-modal'
import { DbInitService } from './shared/services/db-init-service'
import { NetworkService } from './shared/services/network-service'
import { SyncService } from './shared/services/sync-service'
import { DashboardService } from './shared/services/dashboard.service'
import { LogoService } from './shared/services/logo.service'
import { TenantService } from './shared/services/tenant-service'
import { NotificationSocketService } from './shared/services/notification-socket-service'
import { SocketService } from './shared/services/socket-service'
import { LazyLoadRecoveryService } from './shared/services/lazy-load-recovery.service'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  isAdminLogin: boolean = false;
  @ViewChild('informationModal') informationModal!: InfoModal;
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private router: Router,
    private apiLoaderService: ApiLoaderService,
    private translationService: TranslationService,
    private sessionService: SessionService,
    private themeService: ThemeService,
    private dbInitService: DbInitService,
    private networkService: NetworkService,
    private syncService: SyncService,
    private dashboardService: DashboardService,
    private logoService: LogoService,
    private tenantService: TenantService,
    private socketService: SocketService,
    private notificationSocketService: NotificationSocketService,
    private swUpdate: SwUpdate,
    private lazyLoadRecoveryService: LazyLoadRecoveryService
  ) {

    dbInitService.init();           // Initialize DB
    networkService.init();          // Monitor network
    lazyLoadRecoveryService.init(); // Recover lazy routes that failed to load while offline
    // syncService.init();             // Auto-sync offline forms on reconnect
  }

  ngOnInit() {

    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => {

        if (confirm('New version available. Update now?')) {
          window.location.reload();
        }

      });
    // Load tenant logo first
    if (!window.location.pathname.includes('/admin')) this.loadTenantLogo();
    this.initializeTranslation()
    this.initializeTheme()
    this.sessionService.sessionExpired$.subscribe(() => {
      if (!this.router.url.includes('login')) {
        this.openSessionExpiredDialog();
      }
    });

    const token = localStorage.getItem('token')
    const temp_token = localStorage.getItem('temp_token')
    this.isAdminLogin = localStorage.getItem('super_admin') === '/admin';

    if (token || temp_token) {
      if (token && !this.isAdminLogin) {
        this.apiLoaderService.loadPermissionBasedApis(undefined, true);
      }
      let lastRoute = localStorage.getItem('lastRoute') || '/dashboard';
      if (this.isAdminLogin && !lastRoute.startsWith('/admin')) {
        lastRoute = '/admin/dashboard';
      }

      // Avoid redirect loop if user is already on the route
      // if (this.router.url !== lastRoute) {
      //   this.router.navigateByUrl(lastRoute);
      // }
    } else {
      const currentRoute = window.location.pathname || this.router.url;
      if (currentRoute === '/login' || currentRoute === '/admin/login' || currentRoute.startsWith('/admin/login')) {
        if (currentRoute.startsWith('/admin')) {
          this.router.navigate(['/admin/login']);
        } else {
          localStorage.removeItem('super_admin');
          this.router.navigate(['/login']);
        }
      }
    }

    this.authService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (user) {
          // Connect socket first
          this.socketService.connect();

          // Initialize notification service (it will wait for connection internally)
          this.notificationSocketService.init();
        } else {
          this.socketService.disconnect();
          this.notificationSocketService.cleanup();
        }
      });



    // Listen for role change events to refresh APIs with new permissions
    this.setupRoleChangeListener();
  }

  openSessionExpiredDialog() {
    this.informationModal.openModal()
  }

  onCloseExpirationModal() {
    const wasAdmin = this.authService.isAdminUser();
    this.authService.beginLogout();
    this.authService.clearUser();
    const loginRoute = wasAdmin ? '/admin/login' : '/login';
    this.router.navigate([loginRoute]);
  }
  ngOnDestroy() {
    // Clean up event listener
    window.removeEventListener('roleChanged', this.onRoleChanged);

    // Complete all subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupRoleChangeListener() {
    window.addEventListener('roleChanged', this.onRoleChanged);
  }

  private onRoleChanged = (event: Event) => {
    const customEvent = event as CustomEvent;

    // Refresh APIs based on new role's permissions using the shared service
    this.apiLoaderService.loadPermissionBasedApis();
  }

  private initializeTranslation() {
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'english'
    this.translationService.setLanguage(savedLanguage)

    // Apply initial direction
    const isRTL = savedLanguage === 'arabic'
    const direction = isRTL ? 'rtl' : 'ltr'
    document.documentElement.setAttribute('dir', direction)
    document.documentElement.setAttribute('lang', savedLanguage)

    if (isRTL) {
      document.body.classList.add('rtl')
      document.body.classList.remove('ltr')
    } else {
      document.body.classList.add('ltr')
      document.body.classList.remove('rtl')
    }
  }

  private initializeTheme() {
    this.themeService.reapplyTheme();
  }

  private loadTenantLogo(): void {
    const tenant = this.tenantService.getTenant();
    if (!tenant) {
      return;
    }

    this.dashboardService.getTenantLogo().subscribe({
      next: (response) => {
        if (response?.data) {
          if (response.data.logo_url) {
            const logoUrl = response.data.logo_url;
            this.logoService.setLogoUrl(logoUrl);
          }
          const colorTheme = response.data.color_theme;
          if (colorTheme) {
            const themeColor = response.data.color_theme;
            this.themeService.setThemeColor(themeColor);
          }
        }
      },
      error: (err) => {
        console.warn('Failed to load tenant logo and theme:', err);
      }
    });
  }

  protected title = 'dm-flow-frontend'
}
